import { useEffect, useState } from 'react';
import { useAccountSettings } from '@/lib/account-settings';
import { deleteMemory, listMemories } from '@/settings';

/** A person's click uses the same owner-scoped management route as Settings. */
export function useMemoryUndo(account: string, tool: string, result: unknown) {
  const control = useAccountSettings();
  const [state, setState] = useState<'idle' | 'undoing' | 'undone' | 'failed'>('idle');
  const [current, setCurrent] = useState(false);
  const memory =
    tool === 'save_memory' && result && typeof result === 'object' && 'memory' in result
      ? (result.memory as { id?: unknown; content?: unknown })
      : null;
  const id = typeof memory?.id === 'string' ? memory.id : null;
  const content = typeof memory?.content === 'string' ? memory.content : null;
  const own = control?.userId === account;
  useEffect(() => {
    let active = true;
    setCurrent(false);
    setState('idle');
    if (own && id && content)
      void listMemories()
        .then((rows) => {
          if (active) setCurrent(rows.some((row) => row.id === id && row.content === content));
        })
        .catch(() => {
          /* Manage remains available. */
        });
    return () => {
      active = false;
    };
  }, [account, own, id, content]);
  const undoable = own && current && state !== 'undone';
  const undo = async () => {
    if (!undoable || !id || state === 'undoing') return;
    setState('undoing');
    try {
      const rows = await listMemories();
      if (!rows.some((row) => row.id === id && row.content === content)) {
        setCurrent(false);
        setState('failed');
        return;
      }
      await deleteMemory(id);
      setCurrent(false);
      setState('undone');
    } catch {
      setState('failed');
    }
  };
  return { undoable, undo, state };
}
